import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { transactionAPI } from "../api/subscriptionAPI";

// Query keys
const TRANSACTION_KEYS = {
  all: ['transactions'],
  lists: () => [...TRANSACTION_KEYS.all, 'list'],
  list: (filters) => [...TRANSACTION_KEYS.lists(), filters],
  details: () => [...TRANSACTION_KEYS.all, 'detail'],
  detail: (id) => [...TRANSACTION_KEYS.details(), id],
  stats: () => [...TRANSACTION_KEYS.all, 'stats'],
  user: (userId) => [...TRANSACTION_KEYS.all, 'user', userId]
};

/**
 * Hook to fetch all transactions with filtering and pagination
 */
export const useTransactions = (params = {}) => {
  return useQuery({
    queryKey: TRANSACTION_KEYS.list(params),
    queryFn: () => transactionAPI.getAllTransactions(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch transaction statistics
 */
export const useTransactionStats = (params = {}) => {
  return useQuery({
    queryKey: TRANSACTION_KEYS.stats(),
    queryFn: () => transactionAPI.getTransactionStats(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch a single transaction by ID
 */
export const useTransaction = (id) => {
  return useQuery({
    queryKey: TRANSACTION_KEYS.detail(id),
    queryFn: () => transactionAPI.getTransactionById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch user transactions
 */
export const useUserTransactions = (userId, params = {}) => {
  return useQuery({
    queryKey: TRANSACTION_KEYS.user(userId),
    queryFn: () => transactionAPI.getUserTransactions(userId, params),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new transaction
 */
export const useCreateTransaction = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: transactionAPI.createTransaction,
    onSuccess: () => {
      // Invalidate transaction lists and stats
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.stats() });
    },
  });
};

/**
 * Hook to update transaction status
 */
export const useUpdateTransactionStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status, reason }) => 
      transactionAPI.updateTransactionStatus(id, status, reason),
    onSuccess: (data, variables) => {
      // Invalidate transaction lists and the specific transaction
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.stats() });
    },
  });
};

/**
 * Hook to process transaction refund
 */
export const useProcessRefund = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, amount, reason }) => 
      transactionAPI.processRefund(id, amount, reason),
    onSuccess: (data, variables) => {
      // Invalidate transaction lists and the specific transaction
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: TRANSACTION_KEYS.stats() });
    },
  });
};

/**
 * Hook to export transactions
 */
export const useExportTransactions = () => {
  return useMutation({
    mutationFn: transactionAPI.exportTransactions,
    onSuccess: (data, variables) => {
      // Create a blob from the data and trigger download
      const blob = new Blob([data], { 
        type: variables.format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.${variables.format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};

/**
 * Hook to generate transaction receipt
 */
export const useGenerateReceipt = () => {
  return useMutation({
    mutationFn: (id) => transactionAPI.generateReceipt(id),
  });
};

/**
 * Hook to send receipt email
 */
export const useSendReceiptEmail = () => {
  return useMutation({
    mutationFn: (id) => transactionAPI.sendReceiptEmail(id),
  });
};

export default {
  useTransactions,
  useTransactionStats,
  useTransaction,
  useUserTransactions,
  useCreateTransaction,
  useUpdateTransactionStatus,
  useProcessRefund,
  useGenerateReceipt,
  useSendReceiptEmail,
  useExportTransactions
};